import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../api/client";
import { postTrigger } from "../auth/useTriggerRun";

// Shared idea-entry form. Used both as a dedicated page (/new) and
// inline on the empty-state dashboard. The form itself is just an
// auto-growing textarea + a submit affordance — the heading/explainer
// chrome is left to the caller.
//
// Submission flow:
//   1. POST /api/v1/products with the idea -> 201 Product
//   2. POST /api/v1/products/{id}/runs/trigger -> 202 { run_id, stream_url }
//   3. navigate to /products/{id} with stream_url passed in router state
//      so the destination page connects directly to SSE without an extra
//      fetch.
// product_type is intentionally NOT collected here — backend defaults it,
// analyze can refine it. Asking founders to pick product_type up-front
// is the kind of friction that prevents them from starting at all.
export function IdeaForm({
  variant = "page",
  examples,
}: {
  variant?: "page" | "inline";
  examples?: string[];
}) {
  const [idea, setIdea] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const submit = useMutation({
    mutationFn: async (ideaText: string) => {
      const create = await apiClient.POST("/api/v1/products", {
        params: { header: { "X-Requested-With": "xhr" } },
        body: { idea: ideaText },
      });
      if (create.error || !create.data?.id) {
        throw create.error ?? new Error("create product failed");
      }
      const productId = create.data.id;

      // Auto-trigger first analysis. We don't surface failures from this
      // step as form errors — the product was already created (the user's
      // idea is saved). On trigger failure we route to the product page
      // anyway; the pending-first-analysis view then offers a manual
      // retry. Common failure: 402 out-of-credits.
      let streamUrl: string | undefined;
      let runId: string | undefined;
      let triggerError: TriggerError | undefined;
      try {
        const started = await postTrigger(productId);
        streamUrl = started?.stream_url;
        runId = started?.run_id;
      } catch (err) {
        const refused = err as Partial<TriggerError> | null | undefined;
        triggerError =
          typeof refused?.status === "number"
            ? { status: refused.status, envelope: refused.envelope, retryAfter: refused.retryAfter }
            : { status: 0, envelope: { error: { code: "network", message: String(err) } } };
      }

      return { productId, streamUrl, runId, triggerError };
    },
    onSuccess: ({ productId, streamUrl, runId, triggerError }) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate(`/products/${productId}`, {
        state: { streamUrl, runId, triggerError },
      });
    },
  });

  const trimmed = idea.trim();
  const canSubmit = trimmed.length >= 4 && !submit.isPending;

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmit) return;
    submit.mutate(trimmed);
  };

  // Cmd/Ctrl+Enter submits (chat-app convention for multiline inputs)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div
        className={`relative rounded-lg border ${
          submit.isPending ? "border-ink-700" : "border-ink-700 hover:border-ink-600 focus-within:border-accent/60"
        } bg-ink-900/60 transition-colors`}
      >
        <textarea
          autoFocus
          rows={variant === "page" ? 5 : 4}
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={submit.isPending}
          placeholder="An AI-driven CRM for solo consultants. Or anything else you want to spec."
          className="w-full resize-none bg-transparent px-5 py-4 text-base text-ink-100 placeholder:text-ink-500 focus:outline-none disabled:opacity-60"
        />

        <div className="flex items-center justify-between gap-3 border-t border-ink-800 px-3 py-2">
          <span className="font-mono text-[11px] text-ink-400">
            {submit.isPending ? (
              <span className="text-accent">starting analysis…</span>
            ) : trimmed.length === 0 ? (
              <>
                <span className="text-ink-500">⌘</span>+
                <span className="text-ink-500">↵</span> to begin
              </>
            ) : (
              <>{trimmed.length} characters</>
            )}
          </span>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-1.5 text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            {submit.isPending ? "starting…" : "Begin"}
            <span className="opacity-60">→</span>
          </button>
        </div>
      </div>

      {submit.error && (
        <p className="mt-3 text-sm text-danger">
          Couldn't save that idea. {(submit.error as Error).message}
        </p>
      )}

      {examples && examples.length > 0 && trimmed.length === 0 && (
        <div className="mt-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
            For example
          </p>
          <div className="flex flex-wrap gap-2">
            {examples.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setIdea(ex)}
                disabled={submit.isPending}
                className="rounded-md border border-ink-800 hover:border-ink-600 hover:text-ink-100 px-3 py-1.5 text-xs text-ink-300 transition-colors disabled:opacity-50"
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}
    </form>
  );
}

export type TriggerError = {
  status: number;
  envelope: unknown;
  retryAfter?: string | null;
};
